import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../../../security/services/authentication.service';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ProjectAgreement } from '../models/project-agreement.model';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { HttpClient, HttpResponse } from '@angular/common/http';

@Injectable()
export class SignaturesManageService {
  private cancellableDelay = 1000;
  private readonly getAgreementsUrl = `${
    environment.apiUrl
  }/agreement/datatables`;
  private readonly putCancelAgreement = `${
    environment.apiUrl
  }/agreement/cancel/{projectAgreementId}`;
  private readonly putVoidAgreement = `${
    environment.apiUrl
  }/agreement/void/{projectAgreementId}`;
  private readonly putResendAgreement = `${
    environment.apiUrl
  }/agreement/resend/{projectAgreementId}`;
  private readonly postReminderAgreement = `${
    environment.apiUrl
  }/agreement/reminder/{projectAgreementId}`;

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    private swal: SwalService
  ) {}

  cancelAgreement(projectAgreementId: string) {
    const that = this;
    const url = this.putCancelAgreement.replace(
      '{projectAgreementId}',
      projectAgreementId
    );
    return new Observable(subscriber => {
      // Put error handler into a closure since we're going to use it multiple times here
      const errorHandler = () => {
        // Manage subscriber
        subscriber.error();
        subscriber.complete();

        // Alert user (also dismisses loader swal)
        that.swal.error({
          title: 'Oops!',
          text:
            'An error occurred while cancelling your agreement. Please try again later.'
        });
      };

      that.swal.load('Cancelling agreement...');
      that.http
        .put(url, null, that.authService.getAuthOptions())
        .pipe(
          map((res: any) => {
            // The response converts into a boolean that indicates the success status of the request
            return true;
          })
        )
        .subscribe(didSucceed => {
          if (didSucceed) {
            // Success handler

            // Alert user (dismiss loader swal)
            that.swal.success({
              title: 'Successfully cancelled agreement!',
              text: 'You can still resend the agreement if required.'
            });
            // Manage subscriber
            subscriber.next();
            subscriber.complete();
          } else {
            errorHandler();
          }
        }, errorHandler);
    });
  }

  voidAgreement(projectAgreementId: string) {
    const that = this;
    const url = this.putVoidAgreement.replace(
      '{projectAgreementId}',
      projectAgreementId
    );
    return new Observable(subscriber => {
      // Put error handler into a closure since we're going to use it multiple times here
      const errorHandler = () => {
        // Manage subscriber
        subscriber.error();
        subscriber.complete();

        // Alert user (also dismisses loader swal)
        that.swal.error({
          title: 'Oops!',
          text:
            'An error occurred while voiding your agreement. Please try again later.'
        });
      };

      that.swal.load('Voiding agreement...');
      that.http
        .put(url, null, that.authService.getAuthOptions())
        .pipe(
          map((res: any) => {
            // The response converts into a boolean that indicates the success status of the request
            return true;
          })
        )
        .subscribe(didSucceed => {
          if (didSucceed) {
            // Success handler

            // Manage subscriber
            subscriber.next();
            subscriber.complete();

            // Alert user (dismiss loader swal)
            that.swal.success({
              title: 'Successfully voided the agreement!',
              text: 'You can still resend the agreement if required.'
            });
          } else {
            errorHandler();
          }
        }, errorHandler);
    });
  }
  getAgreementsForDatatableCancellable(dataFilters: any) {
    // -------------------------------OVERVIEW-----------------------------------
    // This function delays calling the database in case another request happens.
    // This was done in an effort to minimize going to the database
    // everytime that the user typed in the search box or changed any search criteria quickly.
    // Prior to this, many requests were sent to the api
    // Create a timer object which will be used to cancel the http request to fetch the data.

    // This is the timer which can be cancelled if the user affects the request within the specified DELAY
    let timerObj: any;

    const that = this;

    // This function immediately returns an Observable. The observable is passed a callback which is datatables.net data handler to bind
    // the data to the table.
    return new Observable<{
      data: ProjectAgreement[];
      draw: number;
      recordsFiltered: number;
      recordsTotal: number;
    }>(
      // This is the function or lambda that is called when the .subscribed() function is executed.
      subscriber => {
        timerObj = setTimeout(() => {
          this.http
            .post(
              this.getAgreementsUrl,
              dataFilters,
              this.authService.getAuthOptions()
            )
            .pipe(
              map(
                (res: {
                  data: ProjectAgreement[];
                  draw: number;
                  recordsFiltered: number;
                  recordsTotal: number;
                }) => {
                  if (res) {
                    const datatablesData = res;
                    datatablesData.data = datatablesData.data.map(
                      el => new ProjectAgreement(el)
                    );
                    return datatablesData;
                  }

                  return {
                    data: [],
                    draw: 0,
                    recordsFiltered: 0,
                    recordsTotal: 0
                  };
                }
              )
            )
            .subscribe(
              datatablesData => {
                // This when the subscription or subscriber is notified.
                // Essentially, the a multiple values can be returned at different times, but
                // this will only return one and then close.
                subscriber.next(datatablesData);
                subscriber.complete();
              },
              error => {
                subscriber.error(error);
                subscriber.complete();
              }
            );
        }, that.cancellableDelay);

        // This is the function that is executed when the .unsubscribed() function is executed. The instance of the subscription will
        // have the definition below. When unsubscribed() is called, the timeout will be cleared and therefore the lambda declared inside
        // will not be executed.
        return () => {
          clearTimeout(timerObj);
        };
      }
    );
  }

  sendReminderForAgreement(projectAgreementId: string) {
    const that = this;
    const url = this.postReminderAgreement.replace(
      '{projectAgreementId}',
      projectAgreementId
    );

    return new Observable(subscriber => {
      // Put error handler into a closure since we're going to use it multiple times here
      const errorHandler = () => {
        // Manage subscriber
        subscriber.error();
        subscriber.complete();

        // Alert user (also dismisses loader swal)
        that.swal.error({
          title: 'Oops!',
          text:
            'An error occurred while resending your agreement. Please try again later.'
        });
      };

      that.swal.load('Sending agreement reminder...');
      that.http
        .post(url, null, that.authService.getAuthOptions())
        .pipe(
          map((res: any) => {
            return res;
          })
        )
        .subscribe(didSucceed => {
          if (didSucceed) {
            // Success handler
            console.log(didSucceed);
            // Manage subscriber
            subscriber.next();
            subscriber.complete();
            if(didSucceed && didSucceed.length > 0) {
              // Alert user (dismiss loader swal)
              that.swal.success({
                title: 'Successfully send agreement reminder!',
                text: `Email Sent To: ${didSucceed.join(', ')}`
              });
            }

          } else {
            errorHandler();
          }
        }, errorHandler);
    });

  }

  resendAgreement(projectAgreementId: string) {
    const that = this;
    const url = this.putResendAgreement.replace(
      '{projectAgreementId}',
      projectAgreementId
    );
    return new Observable(subscriber => {
      // Put error handler into a closure since we're going to use it multiple times here
      const errorHandler = () => {
        // Manage subscriber
        subscriber.error();
        subscriber.complete();

        // Alert user (also dismisses loader swal)
        that.swal.error({
          title: 'Oops!',
          text:
            'An error occurred while resending your agreement. Please try again later.'
        });
      };

      that.swal.load('Resending agreement...');
      that.http
        .put(url, null, that.authService.getAuthOptions())
        .pipe(
          map((res: any) => {
            // The response converts into a boolean that indicates the success status of the request
            return res ? true : false;
          })
        )
        .subscribe(didSucceed => {
          if (didSucceed) {
            // Success handler

            // Manage subscriber
            subscriber.next();
            subscriber.complete();

            // Alert user (dismiss loader swal)
            that.swal.success({
              title: 'Successfully resent agreement!'
            });
          } else {
            errorHandler();
          }
        }, errorHandler);
    });
  }
}
